import type { CheckboxOption } from '@archipro-design/aria';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import { Checkbox, Flex } from '@archipro-design/aria';
import type { FilterStatesKey } from '../../type/filter-state';
import { useScrollIntoViewMount } from '../../hook/use-scroll-into-view-mount';

export interface RadioOption extends CheckboxOption {
    isDefault: boolean;
}
interface RadioGroupProps {
    items: RadioOption[];
    selectedItem?: RadioOption | null;
}

export interface RadioGroupFilter {
    kind: 'RadioGroup';
    filterKey: FilterStatesKey;
    subFilterKey?: string;
    props: RadioGroupProps;
}

export type RadioGroupFilterState = Pick<
    RadioGroupProps,
    'items' | 'selectedItem'
> &
    Pick<RadioGroupFilter, 'kind'>;

interface FilterRadioGroupProps {
    filter: RadioGroupFilter;
    state: RadioGroupFilterState;
    updateFilter: (
        filterKey: FilterStatesKey,
        state: Partial<RadioGroupFilterState>,
    ) => void;
    disabled?: boolean;
}

const FilterRadioGroup = ({
    filter,
    state,
    updateFilter,
    disabled,
}: FilterRadioGroupProps) => {
    const { props, filterKey } = filter;
    const items = state.items ?? props.items;
    const selectedItem = state.selectedItem ?? items[0];

    const { desktop } = useAppDisplayModeContext();
    const { elementRef } = useScrollIntoViewMount({ active: desktop });

    return (
        <Flex column gap="space-8" ref={elementRef}>
            {items.map((item) => (
                <Checkbox
                    key={item.label}
                    disabled={disabled}
                    label={{ children: item.label, variant: '02' }}
                    checked={item.value === selectedItem?.value}
                    indicator={{ kind: 'radio' }}
                    onChange={() => {
                        if (item === selectedItem) return;
                        updateFilter(filterKey, {
                            kind: 'RadioGroup',
                            selectedItem: item,
                        });
                    }}
                />
            ))}
        </Flex>
    );
};

export default FilterRadioGroup;
